import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import VerifiedBadge from "../shared/VerifiedBadge";
import useMediaQuery from "../../hooks/useMediaQuery";
import { useAuth } from "../../contexts/AuthContext";
import { reportShop } from "../../api/shops";
import { shopPath } from "../../utils/shopPath";
import { REVIEWS_ANCHOR_ID, Stars } from "./ShopReviews";

/* The top of a shop page. Simplified 2026-10-19 to the owner's screenshot:
   ONE card with the logo (a rounded square, as tall as the text beside it),
   the shop name and seal, the @handle and the description. Nothing else is
   in the card: no banner, stats, meta, follow or "Official Emporia store"
   label (the seal's explainer still says it for Emporia's own store).
   "Report this shop" (owner: "Edit shop info") sits in the card under the
   description. No share, no tabs (owner, 2026-10-19).

   Owner controls (Change shop logo, Edit shop info) appear ONLY when the
   server says is_owner: it decides from the caller's token, never this page.

   Customers never contact sellers on Emporia: there is no message button.
   "Report this shop" goes to Emporia's support team. */

export const SHOP_LOOK_PATH = "/sell/portal/shop";
export const DESCRIPTION_MAX = 300;
export const MOBILE_QUERY = "(max-width: 640px)";

const longDate = (iso) => {
  if (!iso) return null;
  const d = new Date(`${iso}T00:00:00Z`);
  return Number.isNaN(d.getTime()) ? null : d.toLocaleDateString("en-KE", { day: "numeric", month: "long", year: "numeric", timeZone: "UTC" });
};

const Icon = ({ children, size = 18, fill = "none", strokeWidth = 2 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill={fill} stroke={fill === "none" ? "currentColor" : "none"} strokeWidth={strokeWidth}
       strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">{children}</svg>
);
const CameraIcon = () => <Icon strokeWidth={2.2}><path d="M4 8h3l2-3h6l2 3h3v11H4z" /><circle cx="12" cy="13" r="3.5" /></Icon>;
const PencilIcon = () => <Icon><path d="M4 20h4L19 9l-4-4L4 16z" /><path d="M14 6l4 4" /></Icon>;
const FlagIcon = () => <Icon size={14}><path d="M5 21V4" /><path d="M5 4h11l-2 4 2 4H5" /></Icon>;

// Down to the reviews section, smoothly unless the visitor asked for less
// motion; focus moves to its heading so a keyboard or screen-reader user
// lands there too.
function goToReviews() {
  const section = document.getElementById(REVIEWS_ANCHOR_ID);
  if (!section) return;
  const still = typeof window.matchMedia === "function" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  section.scrollIntoView?.({ behavior: still ? "auto" : "smooth", block: "start" });
  document.getElementById(`${REVIEWS_ANCHOR_ID}-title`)?.focus({ preventScroll: true });
}

/** The shop's rating: the same verified reviews the reviews section shows
    (backend shop_hero.review_summary), so the numbers always match. */
export function ShopRating({ reviews }) {
  if (!reviews) return null;
  if (!reviews.count || reviews.average == null) {
    return <p className="shop-rating shop-rating--none">No reviews yet</p>;
  }
  const n = reviews.count.toLocaleString("en-KE");
  const label = `${n} ${reviews.count === 1 ? "review" : "reviews"}`;
  return (
    <button
      type="button"
      className="shop-rating"
      onClick={goToReviews}
      aria-label={`Rated ${reviews.average.toFixed(1)} out of 5 from ${label}. Go to the reviews`}
    >
      <Stars rating={reviews.average} className="shop-rating-stars" />
      <span className="shop-rating-number" aria-hidden="true">{reviews.average.toFixed(1)}</span>
      <span className="shop-rating-count" aria-hidden="true">({label})</span>
    </button>
  );
}

export function ShopLogo({ shop, className = "" }) {
  const letter = (String(shop.display_name || shop.handle || "").replace(/^@/, "")[0] || "?").toUpperCase();
  if (shop.avatar_url) {
    const srcSet = shop.avatar_small_url ? `${shop.avatar_small_url} 160w, ${shop.avatar_url} 400w` : undefined;
    return (
      <span className={`shop-logo ${className}`}>
        <img src={shop.avatar_url} srcSet={srcSet} sizes="(max-width: 640px) 120px, 220px" alt="" />
      </span>
    );
  }
  return <span className={`shop-logo shop-logo--initial ${className}`} aria-hidden="true">{letter}</span>;
}

// Only what the system records: the badge is set by Emporia's team, and
// every purchase goes through Emporia's checkout. No partnership claim.
export function BadgeExplainer({ shop, onClose, returnFocusTo }) {
  const panelRef = useRef(null);
  useEffect(() => {
    const panel = panelRef.current;
    panel?.focus();
    const onKey = (e) => {
      if (e.key === "Escape") { e.preventDefault(); onClose(); return; }
      if (e.key !== "Tab" || !panel) return;
      const focusable = panel.querySelectorAll("button, a[href]");
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    };
    document.addEventListener("keydown", onKey);
    const returnTo = returnFocusTo.current;
    return () => { document.removeEventListener("keydown", onKey); returnTo?.focus(); };
  }, [onClose, returnFocusTo]);

  const date = longDate(shop.verified_at);
  return (
    <div className="shop-explainer" role="dialog" aria-modal="true" aria-labelledby="shop-explainer-title" ref={panelRef} tabIndex={-1}>
      <p className="shop-explainer-title" id="shop-explainer-title">
        <VerifiedBadge size="20px" /> {shop.is_platform ? "Official Emporia store" : "Emporia verified seller"}
      </p>
      {shop.is_platform ? <p>This is Emporia's own store.</p> : <p>Emporia's team checked this seller before marking the shop as verified.</p>}
      {date && <p className="shop-explainer-date">Verified on {date}.</p>}
      <p>Whoever you buy from, you pay through Emporia's checkout, and your money is held until you confirm delivery.</p>
      <button type="button" className="shop-btn shop-btn--ghost" onClick={onClose}>Close</button>
    </div>
  );
}

export function ReportForm({ shop, onCancel }) {
  const { token } = useAuth() || {};
  const [text, setText] = useState("");
  const [state, setState] = useState("idle");   // idle | sending | sent | error
  const [error, setError] = useState("");

  const send = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    setState("sending");
    setError("");
    try {
      await reportShop(shop.handle, text.trim(), token);
      setState("sent");
    } catch (err) {
      setState("error");
      setError(err.message || "We couldn't send your report. Please try again.");
    }
  };

  if (state === "sent") {
    return (
      <p className="shop-report-sent" role="status">
        Thank you. Your report has gone to Emporia's support team, not to the seller. You'll find our reply in your inbox.
      </p>
    );
  }
  return (
    <form className="shop-report" onSubmit={send}>
      <label htmlFor="shop-report-text">What's wrong with {shop.display_name}? Emporia's support team reads this; the seller doesn't.</label>
      <textarea id="shop-report-text" rows={3} maxLength={2000} value={text} onChange={(e) => setText(e.target.value)} />
      {error && <p className="shop-error" role="alert">{error}</p>}
      <div className="shop-report-actions">
        <button type="submit" className="shop-btn" disabled={state === "sending" || !text.trim()}>
          {state === "sending" ? "Sending…" : "Send report"}
        </button>
        <button type="button" className="shop-btn shop-btn--ghost" onClick={onCancel}>Cancel</button>
      </div>
    </form>
  );
}

// The logo box is as tall as the text beside it (owner, 2026-10-19) by CSS
// alone: a fixed-width column whose box stretches to the row's height, and
// is never shorter than it is wide (ShopHero.css). Nothing is measured, so
// nothing can feed back: a logo that grew with the text would narrow the
// text, which would wrap and grow again (seen on a 390px phone).

/** The card itself: also the live preview in the seller's Shop look
    (preview: no buttons, no heading). */
export function ShopIdCard({ shop, owner = false, preview = false }) {
  const isMobile = useMediaQuery(MOBILE_QUERY);
  const { token } = useAuth() || {};
  const navigate = useNavigate();
  const [reporting, setReporting] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [explainer, setExplainer] = useState(false);
  const badgeButton = useRef(null);
  const closeExplainer = useCallback(() => setExplainer(false), []);

  const description = shop.description || "";
  const long = description.length > DESCRIPTION_MAX;
  const shown = long && !expanded ? `${description.slice(0, DESCRIPTION_MAX).replace(/\s+\S*$/, "")}…` : description;
  const handleText = `@${shop.shop_name || shop.handle}`;
  const Name = preview ? "p" : "h1";
  // The seal travels with the name's last word, never alone on a line.
  const name = String(shop.display_name || "");
  const cut = name.lastIndexOf(" ");
  const nameHead = cut > 0 ? name.slice(0, cut + 1) : "";
  const nameTail = cut > 0 ? name.slice(cut + 1) : name;

  const startReport = () => {
    if (!token) {
      navigate(`/login?redirect=${encodeURIComponent(shopPath(shop.handle))}`);
      return;
    }
    setReporting(true);
  };

  return (
    <div className="shop-id-card">
      <div className="shop-id-logo-wrap">
        <ShopLogo shop={shop} className="shop-id-logo" />
        {owner && (
          <Link to={`${SHOP_LOOK_PATH}#logo`} className="shop-owner-logo-btn" aria-label="Change shop logo"><CameraIcon /></Link>
        )}
      </div>
      <div className="shop-id-info">
        <Name className="shop-hero-name">
          {nameHead}
          <span className="shop-hero-name-tail">{nameTail}
          {shop.is_verified && (preview ? (
            <span className="shop-badge-btn"><VerifiedBadge size={isMobile ? "22px" : "28px"} /></span>
          ) : (
            <button
              type="button"
              className="shop-badge-btn"
              ref={badgeButton}
              aria-label={shop.is_platform ? "Official Emporia store: what this means" : "Emporia verified seller: what this means"}
              aria-expanded={explainer}
              onClick={() => setExplainer((v) => !v)}
            >
              <VerifiedBadge size={isMobile ? "22px" : "28px"} />
            </button>
          ))}
          </span>
        </Name>
        {shop.display_name !== handleText && <p className="shop-hero-handle">{handleText}</p>}
        {explainer && <BadgeExplainer shop={shop} onClose={closeExplainer} returnFocusTo={badgeButton} />}
        {description && (
          <p className="shop-hero-description">
            {shown}
            {long && (
              <>
                {" "}
                <button type="button" className="shop-link-btn" aria-expanded={expanded} onClick={() => setExpanded((v) => !v)}>
                  {expanded ? "Show less" : "Read more"}
                </button>
              </>
            )}
          </p>
        )}
        {!preview && <ShopRating reviews={shop.reviews} />}
        {reporting && !owner && !preview && <ReportForm shop={shop} onCancel={() => setReporting(false)} />}
      </div>
      {/* Top right of the card (owner, 2026-10-20). */}
      {!preview && (
        <div className="shop-id-action">
          {owner ? (
            <Link to={`${SHOP_LOOK_PATH}#details`} className="shop-card-link"><PencilIcon />Edit shop info</Link>
          ) : (
            <button type="button" className="shop-card-link" onClick={startReport} aria-expanded={reporting}><FlagIcon />Report this shop</button>
          )}
        </div>
      )}
    </div>
  );
}

const ShopHero = ({ shop }) => (
  <section className="shop-hero" aria-label="Shop profile">
    <ShopIdCard shop={shop} owner={Boolean(shop.is_owner)} />
  </section>
);

export default ShopHero;
